<template>
    <div>
        <div v-if="isLoading" class="alert alert-info mt-0">
            <p>Loading...</p>
        </div>
        <div v-else>

            <div v-if="needsDob">

                <p>
                    In order to verify your identity, we need your date of birth.
                </p>
                <dob-update-component :user="user" @dob-updated="fetchUser" />

            </div>
            <div v-else>

                <div v-if="isSubmitted" class="alert alert-success mt-0">
                    <p class="mb-2">Thank you.</p>
                    <p>{{ message }}</p>
                </div>

                <div v-if="status === 'Approved'" class="alert alert-success mt-0">
                    <p class="mb-2">Your ID verification has been approved.</p>
                    <p>Thank you, there is nothing more you need to do.</p>
                </div>
                <div v-else>

                    <div v-if="hasErrors" class="alert alert-danger mt-0 mb-4">
                        <ul class="mb-0">
                            <li v-for="(messages, field) in errors" :key="field">
                                <span v-for="message in messages" :key="message">{{ message }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="status === 'Rejected'" class="alert alert-danger mt-0 mb-4">
                        <p class="mb-2">Your ID verification was rejected.</p>
                        <p><strong>Reason:</strong> {{ reason }}</p>
                    </div>
                    <div v-if="status === 'Pending'" class="alert alert-info mt-0">
                        <p class="mb-2">Your ID verification is pending approval.</p>
                        <p>Please check back here shortly. We will send you an email when your ID has been reviewed.</p>
                    </div>
                    <div v-if="status === 'not_submitted' || status === 'Rejected'">
                        <div v-if="isMinor" class="alert alert-secondary mb-4">
                            <p>
                                Because you are under 18 we need a responsible adult to upload their ID instead of
                                yours. Please ask them to do so in this form.
                            </p>
                        </div>
                        <div v-else>
                            <p>
                                Sometimes we need to ensure that you are who you say you are. Please provide the following information to verify your identity.
                                We often use this for assessments and other important tasks.
                            </p>
                            <p>
                                Please ensure the name you use on your account matches the one on your ID. Your name on the account is: <strong>{{ user.name }}</strong>.
                                You can change your name on your <a href="/account">account settings</a>.
                            </p>
                        </div>
                        <form @submit.prevent="submitForm">
                            <div class="form-group">
                                <label for="id_type_id" class="form-label text-md-right">ID Type</label>
                                <select v-model="form.id_type_id" id="id_type_id" class="form-control" :class="{ 'is-invalid': errors.id_type_id }">
                                    <option></option>
                                    <option v-for="idType in types" :key="idType.id" :value="idType.id">{{ idType.name }}</option>
                                </select>
                                <span v-if="errors.id_type_id" class="invalid-feedback" role="alert">
                                    <strong>{{ errors.id_type_id[0] }}</strong>
                                </span>
                            </div>
                            <div class="form-group">
                                <label for="file" class="form-label text-md-right">Image</label>
                                <input type="file" @change="handleFileUpload" id="file" class="form-control" :class="{ 'is-invalid': errors.file }" required>
                                <p>
                                    <small class="form-text text-muted">
                                        Please upload a clear image of {{ isMinor ? 'a responsible adult\'s' : 'your' }} ID. We accept JPG, PNG, and PDF files.<br>
                                        Note that we delete any files once they have been reviewed.
                                    </small>
                                </p>
                                <span v-if="errors.file" class="invalid-feedback" role="alert">
                                    <strong>{{ errors.file[0] }}</strong>
                                </span>
                            </div>
                            <template v-if="isMinor">
                                <div class="form-group">
                                    <label for="adult_name" class="form-label text-md-right">Responsible Adult's Name</label>
                                    <input type="text" id="adult_name" class="form-control" v-model="form.adult_name" required>
                                </div>

                                <div class="form-group">
                                    <label for="adult_relationship" class="form-label text-md-right">Relationship to Candidate</label>
                                    <input type="text" id="adult_relationship" class="form-control" v-model="form.adult_relationship" required>
                                </div>
                            </template>

                            <div class="form-group justify-content-center row mt-4 mb-0">
                                <div class="col text-center">
                                    <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                                        {{ submitButtonText }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        types: {
            type: Array,
            required: true,
        }
    },
    data() {
        return {
            form: {
                id_type_id: '',
                file: null,
                adult_name: '',
                adult_relationship: '',
            },
            errors: {},
            hasErrors: false,
            status: '', // Will be 'approved', 'rejected', 'pending', or 'not_submitted'
            reason: '', // Reason for rejection, if any
            isSubmitting: false,
            isLoading: true,
            submitButtonText: 'Submit',
            message: '',
            isSubmitted: false,
            isAuth: false,
            isMinor: false,
            user: null,
            needsDob: true,
        }
    },
    mounted() {
        if(window.virtuoso) {
            if(window.virtuoso.is_auth) {
                this.isAuth = true;
                this.user = window.virtuoso.user;
            }
        }

        this.fetchUser();
    },
    methods: {
        // Get the user
        fetchUser() {
            this.isLoading = true;
            axios.get(`/api/me/get-user`)
                .then(response => {
                    this.user = response.data;

                    if(this.user.dob) {
                        this.needsDob = false;
                        this.isMinor = this.user.isMinor;

                        this.fetchStatus();
                    } else {
                        this.isLoading = false;
                    }
                })
                .catch(error => {
                    console.error('Error fetching user:', error);
                });
        },

        fetchStatus() {
            this.isLoading = true;
            axios.get(`/api/me/id-verification/status`)
                .then(response => {
                    const data = response.data;
                    this.status = data.status;
                    this.reason = data.reason;
                })
                .catch(error => {
                    console.error('Error fetching status:', error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handleFileUpload(event) {
            this.form.file = event.target.files[0];
        },

        submitForm() {
            this.isSubmitting = true;
            this.errors = {};
            this.hasErrors = false;

            const formData = new FormData();
            formData.append('id_type_id', this.form.id_type_id);
            formData.append('file', this.form.file);
            formData.append('adult_name', this.form.adult_name);
            formData.append('adult_relationship', this.form.adult_relationship);

            axios.post(`/api/me/id-verification/process`, formData)
                .then(response => {
                    // Handle success, maybe show a success message or redirect
                    this.isSubmitting = false;
                    this.isSubmitted = true;
                    this.message = response.data.message;
                    this.status = ''; // Hides all messages
                })
                .catch(error => {
                    if (error.response && error.response.data.errors) {
                        this.errors = error.response.data.errors;
                        this.hasErrors = true;
                    }
                    this.isSubmitting = false;
                });
        }
    }
}
</script>
